import { useState, useRef, useEffect } from "react";
import Dashboard from "./Dashboard";

const ANTHROPIC_SYSTEM_PROMPT = `You are LeadQual, an AI assistant for Portuguese property agencies. Your job is to qualify property leads by having a natural conversation.

LANGUAGE RULE: Detect the language of the user's first message. If they write in Portuguese, respond in Portuguese throughout. If they write in English, respond in English throughout. Keep the same language for the entire conversation.

YOUR GOAL: Qualify the lead by naturally gathering these details:
1. Are they looking to BUY or RENT?
2. What type of property? (apartment, villa, house, commercial)
3. Location preference in Portugal (city, region, or area)
4. Budget range
5. Timeline (when do they want to move/buy?)
6. For buyers: are they cash buyers or need financing? First home or investment?
7. For renters: how many people, pets, employed/self-employed?
8. Contact details (name, phone/email) — ask this toward the end

Be warm, conversational and professional — like a helpful human agent, not a robot form. Ask one or two questions at a time, not all at once. Use natural follow-ups.

After you have gathered enough information (at least 5-6 data points), end with a JSON block in this exact format on its own line:
LEAD_SUMMARY_JSON:{"name":"...","intent":"buy|rent","propertyType":"...","location":"...","budget":"...","timeline":"...","additionalNotes":"...","leadScore":"hot|warm|cold","leadScoreReason":"...","contact":"..."}

Score the lead:
- HOT: clear intent, realistic budget, short timeline, responsive
- WARM: interested but vague on timeline or budget  
- COLD: browsing, unrealistic, or unresponsive

Start the conversation with a friendly greeting introducing yourself as an assistant for the agency, and ask how you can help.`;

const translations = {
  en: {
    placeholder: "Type your message...",
    leadSummary: "Lead Summary",
    intent: "Intent",
    property: "Property Type",
    location: "Location",
    budget: "Budget",
    timeline: "Timeline",
    contact: "Contact",
    notes: "Notes",
    reason: "Score Reason",
    newChat: "New Conversation",
    demoTag: "LIVE DEMO",
  },
  pt: {
    placeholder: "Escreva a sua mensagem...",
    leadSummary: "Resumo do Lead",
    intent: "Intenção",
    property: "Tipo de Imóvel",
    location: "Localização",
    budget: "Orçamento",
    timeline: "Prazo",
    contact: "Contacto",
    notes: "Notas",
    reason: "Motivo",
    newChat: "Nova Conversa",
    demoTag: "DEMO AO VIVO",
  },
};

const scoreColors = {
  hot: { bg: "#ff4d00", text: "#fff", label: "🔥 HOT" },
  warm: { bg: "#f5a623", text: "#fff", label: "⚡ WARM" },
  cold: { bg: "#4a90d9", text: "#fff", label: "❄️ COLD" },
};

// ─── Top nav shown on both views ────────────────────────────────────────────
function TopNav({ currentView, onNavigate }) {
  return (
    <div style={styles.topNav}>
      <div style={styles.topNavLogo}>
        <span style={styles.topNavIcon}>⬡</span>
        <span style={styles.topNavText}>LeadQual</span>
      </div>
      <div style={styles.topNavTabs}>
        <button
          style={{ ...styles.topNavTab, ...(currentView === "chatbot" ? styles.topNavTabActive : {}) }}
          onClick={() => onNavigate("chatbot")}
        >
          ◈ Lead Chatbot
        </button>
        <button
          style={{ ...styles.topNavTab, ...(currentView === "dashboard" ? styles.topNavTabActive : {}) }}
          onClick={() => onNavigate("dashboard")}
        >
          ◎ Agency Dashboard
        </button>
      </div>
      <div style={styles.topNavTag}>DEMO</div>
    </div>
  );
}

// ─── Chatbot view ────────────────────────────────────────────────────────────
function ChatbotView() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [lead, setLead] = useState(null);
  const [uiLang, setUiLang] = useState("en");
  const [started, setStarted] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null); // null | "sending" | "sent" | "error"
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const t = translations[uiLang];

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const startChat = async (lang) => {
    setUiLang(lang);
    setStarted(true);
    setLoading(true);
    const greeting = lang === "pt"
      ? "Olá! Por favor, comece em português."
      : "Hello! Please start in English.";
    const res = await callClaude([{ role: "user", content: greeting }]);
    setMessages([{ role: "assistant", content: res }]);
    setLoading(false);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const callClaude = async (msgs, attempt = 1) => {
    try {
      const response = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-sonnet-4-20250514",
          max_tokens: 1000,
          system: ANTHROPIC_SYSTEM_PROMPT,
          messages: msgs,
        }),
      });
      const data = await response.json();
      if (response.status === 529 && attempt < 3) {
        await new Promise(r => setTimeout(r, attempt * 2000));
        return callClaude(msgs, attempt + 1);
      }
      return data.content?.[0]?.text || "Sorry, I couldn't process that.";
    } catch (err) {
      if (attempt < 3) {
        await new Promise(r => setTimeout(r, attempt * 2000));
        return callClaude(msgs, attempt + 1);
      }
      return "Sorry, I couldn't reach the assistant. Please try again in a moment.";
    }
  };

  const extractLead = (text) => {
    const match = text.match(/LEAD_SUMMARY_JSON:(\{.*\})/);
    if (match) { try { return JSON.parse(match[1]); } catch { return null; } }
    return null;
  };

  const cleanText = (text) => text.replace(/LEAD_SUMMARY_JSON:\{.*\}/, "").trim();

  const sendLeadEmail = async (qualifiedLead, transcript) => {
    setEmailStatus("sending");
    try {
      const response = await fetch("/api/send-lead", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          lead: {
            ...qualifiedLead,
            source: "Website Chat",
            transcript: transcript.map(m => [m.role === "assistant" ? "bot" : "user", m.content]),
          },
          agencyEmail: process.env.REACT_APP_AGENCY_EMAIL || "leads@leadqual.io",
          agencyName: process.env.REACT_APP_AGENCY_NAME || "LeadQual Demo Agency",
        }),
      });
      const result = await response.json();
      if (result.success) {
        setEmailStatus("sent");
      } else {
        console.error("Email send failed:", result);
        setEmailStatus("error");
      }
    } catch (err) {
      console.error("Email error:", err);
      setEmailStatus("error");
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMsg = { role: "user", content: input.trim() };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput("");
    setLoading(true);
    const history = newMessages.map((m) => ({ role: m.role, content: m.content }));
    const reply = await callClaude(history);
    const extracted = extractLead(reply);
if (extracted) {
  setLead(extracted);
  if (emailStatus === null) {
    sendLeadEmail(extracted, newMessages);
  }
}
    setMessages([...newMessages, { role: "assistant", content: cleanText(reply) }]);
    setLoading(false);
  };

  const reset = () => {
    setMessages([]);
    setInput("");
    setLead(null);
    setStarted(false);
    setEmailStatus(null);
  };

  if (!started) {
    return (
      <div style={styles.splash}>
        <div style={styles.splashInner}>
          <div style={styles.splashLogo}>⬡</div>
          <h1 style={styles.splashTitle}>LeadQual</h1>
          <p style={styles.splashSub}>AI-powered property lead qualification for Portugal</p>
          <div style={styles.splashDivider} />
          <p style={styles.splashPrompt}>Choose demo language / Escolha o idioma</p>
          <div style={styles.langRow}>
            <button style={styles.langBtn} onClick={() => startChat("en")}>🇬🇧 English</button>
            <button style={styles.langBtnPt} onClick={() => startChat("pt")}>🇵🇹 Português</button>
          </div>
          <p style={styles.splashNote}>The bot will match the language of your leads automatically</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.chatRoot}>
      {/* Sidebar */}
      <div style={styles.sidebar}>
        <div style={styles.sidebarTop}>
          <div style={styles.sidebarLabel}>{t.leadSummary}</div>
          <div style={styles.demoTag}>{t.demoTag}</div>
        </div>
        <div style={styles.sidebarSection}>
          {!lead ? (
            <div style={styles.noLead}>
              <div style={styles.noLeadIcon}>◎</div>
              <p style={styles.noLeadText}>Lead summary will appear here once the conversation qualifies the prospect</p>
            </div>
          ) : (
            <div>
              <div style={styles.leadCard}>
                {lead.leadScore && (
                  <div style={{
                    ...styles.scoreBadge,
                    background: scoreColors[lead.leadScore]?.bg || "#666",
                    color: scoreColors[lead.leadScore]?.text || "#fff",
                  }}>
                    {scoreColors[lead.leadScore]?.label || lead.leadScore.toUpperCase()}
                  </div>
                )}
                {[
                  [t.intent, lead.intent],
                  [t.property, lead.propertyType],
                  [t.location, lead.location],
                  [t.budget, lead.budget],
                  [t.timeline, lead.timeline],
                  [t.contact, lead.contact],
                  [t.notes, lead.additionalNotes],
                  [t.reason, lead.leadScoreReason],
                ].map(([label, val]) => val && (
                  <div key={label} style={styles.leadRow}>
                    <span style={styles.leadLabel}>{label}</span>
                    <span style={styles.leadVal}>{val}</span>
                  </div>
                ))}
              </div>

              {/* Email status indicator */}
              {emailStatus === "sending" && (
                <div style={styles.emailStatus}>
                  <span style={styles.emailStatusDot("#c9a96e")}>●</span>
                  Sending lead to agency...
                </div>
              )}
              {emailStatus === "sent" && (
                <div style={{ ...styles.emailStatus, color: "#4ade80" }}>
                  <span style={{ color: "#4ade80", marginRight: "6px" }}>✓</span>
                  Lead emailed to agency
                </div>
              )}
              {emailStatus === "error" && (
                <div style={{ ...styles.emailStatus, color: "#f87171" }}>
                  <span style={{ marginRight: "6px" }}>⚠</span>
                  Email failed — lead saved locally
                </div>
              )}
            </div>
          )}
        </div>
        <button style={styles.newChatBtn} onClick={reset}>{t.newChat}</button>
      </div>

      {/* Chat area */}
      <div style={styles.chatArea}>
        <div style={styles.chatHeader}>
          <div style={styles.agentDot} />
          <div>
            <div style={styles.agentName}>LeadQual Assistant</div>
            <div style={styles.agentStatus}>Online · Auto-detecting language</div>
          </div>
        </div>
        <div style={styles.messages}>
          {messages.map((m, i) => (
            <div key={i} style={m.role === "user" ? styles.userRow : styles.botRow}>
              {m.role === "assistant" && <div style={styles.botAvatar}>⬡</div>}
              <div style={m.role === "user" ? styles.userBubble : styles.botBubble}>
                {m.content.split("\n").map((line, j) => (
                  <span key={j}>{line}{j < m.content.split("\n").length - 1 && <br />}</span>
                ))}
              </div>
            </div>
          ))}
          {loading && (
            <div style={styles.botRow}>
              <div style={styles.botAvatar}>⬡</div>
              <div style={styles.botBubble}>
                <div style={styles.typingDots}>
                  <span style={{ ...styles.dot, animationDelay: "0s" }} />
                  <span style={{ ...styles.dot, animationDelay: "0.2s" }} />
                  <span style={{ ...styles.dot, animationDelay: "0.4s" }} />
                </div>
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>
        <div style={styles.inputRow}>
          <input
            ref={inputRef}
            style={styles.input}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendMessage()}
            placeholder={t.placeholder}
            disabled={loading}
          />
          <button style={styles.sendBtn} onClick={sendMessage} disabled={loading || !input.trim()}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Root app with navigation ────────────────────────────────────────────────
export default function App() {
  const [currentView, setCurrentView] = useState("chatbot");

  return (
    <div style={styles.appRoot}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=DM+Sans:wght@300;400;500;600&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'DM Sans', sans-serif; }
        @keyframes bounce {
          0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
          40% { transform: translateY(-6px); opacity: 1; }
        }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 4px; }
        select option { background: #1a2d45; color: #fff; }
      `}</style>

      <TopNav currentView={currentView} onNavigate={setCurrentView} />

      <div style={styles.viewContainer}>
        {currentView === "chatbot" && <ChatbotView />}
        {currentView === "dashboard" && <Dashboard />}
      </div>
    </div>
  );
}

const styles = {
  appRoot: {
    display: "flex", flexDirection: "column", height: "100vh",
    fontFamily: "'DM Sans', sans-serif", background: "#0d1926",
  },

  // Top nav
  topNav: {
    display: "flex", alignItems: "center", gap: "16px",
    padding: "0 20px", height: "52px", flexShrink: 0,
    background: "#07111f",
    borderBottom: "1px solid rgba(255,255,255,0.08)",
  },
  topNavLogo: { display: "flex", alignItems: "center", gap: "8px", marginRight: "8px" },
  topNavIcon: { fontSize: "20px", color: "#c9a96e", fontFamily: "'DM Serif Display', serif" },
  topNavText: { fontFamily: "'DM Serif Display', serif", fontSize: "17px", color: "#fff" },
  topNavTabs: { display: "flex", gap: "4px", flex: 1 },
  topNavTab: {
    padding: "6px 16px", borderRadius: "7px", fontSize: "13px", fontWeight: 500,
    background: "none", border: "1px solid transparent",
    color: "rgba(255,255,255,0.4)", cursor: "pointer",
    fontFamily: "'DM Sans', sans-serif", transition: "all 0.15s",
  },
  topNavTabActive: {
    background: "rgba(201,169,110,0.1)",
    border: "1px solid rgba(201,169,110,0.25)",
    color: "#c9a96e",
  },
  topNavTag: {
    fontSize: "9px", fontWeight: 700, letterSpacing: "1.5px",
    color: "#c9a96e", border: "1px solid rgba(201,169,110,0.4)",
    padding: "3px 7px", borderRadius: "4px",
  },

  viewContainer: { flex: 1, overflow: "hidden" },

  // Chatbot layout
  chatRoot: {
    display: "flex", height: "100%",
    background: "#0f1c2e", overflow: "hidden",
  },

  // Splash
  splash: {
    height: "100%",
    background: "linear-gradient(135deg, #0f1c2e 0%, #1a3a5c 50%, #0f1c2e 100%)",
    display: "flex", alignItems: "center", justifyContent: "center",
  },
  splashInner: {
    textAlign: "center", padding: "48px 40px",
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: "24px", maxWidth: "420px", width: "90%",
    backdropFilter: "blur(12px)",
  },
  splashLogo: { fontSize: "52px", color: "#c9a96e", marginBottom: "16px", fontFamily: "'DM Serif Display', serif" },
  splashTitle: { fontFamily: "'DM Serif Display', serif", fontSize: "38px", color: "#fff", marginBottom: "8px" },
  splashSub: { color: "rgba(255,255,255,0.5)", fontSize: "14px", marginBottom: "28px" },
  splashDivider: { height: "1px", background: "rgba(255,255,255,0.1)", margin: "24px 0" },
  splashPrompt: { color: "rgba(255,255,255,0.6)", fontSize: "13px", marginBottom: "16px" },
  langRow: { display: "flex", gap: "12px", justifyContent: "center", marginBottom: "20px" },
  langBtn: {
    padding: "12px 28px", borderRadius: "10px", border: "1px solid rgba(255,255,255,0.2)",
    background: "rgba(255,255,255,0.08)", color: "#fff", fontSize: "15px",
    cursor: "pointer", fontFamily: "'DM Sans', sans-serif", fontWeight: 500,
  },
  langBtnPt: {
    padding: "12px 28px", borderRadius: "10px", border: "1px solid #c9a96e",
    background: "rgba(201,169,110,0.15)", color: "#c9a96e", fontSize: "15px",
    cursor: "pointer", fontFamily: "'DM Sans', sans-serif", fontWeight: 500,
  },
  splashNote: { color: "rgba(255,255,255,0.3)", fontSize: "12px" },

  // Sidebar
  sidebar: {
    width: "300px", minWidth: "300px",
    background: "#0a1628",
    borderRight: "1px solid rgba(255,255,255,0.07)",
    display: "flex", flexDirection: "column",
  },
  sidebarTop: {
    padding: "20px 24px 16px",
    borderBottom: "1px solid rgba(255,255,255,0.07)",
    display: "flex", alignItems: "center", justifyContent: "space-between",
  },
  sidebarLabel: {
    fontSize: "10px", fontWeight: 700, letterSpacing: "2px",
    color: "rgba(255,255,255,0.3)", textTransform: "uppercase",
  },
  demoTag: {
    fontSize: "9px", fontWeight: 700, letterSpacing: "1.5px",
    color: "#c9a96e", border: "1px solid #c9a96e",
    padding: "3px 6px", borderRadius: "4px",
  },
  sidebarSection: { padding: "20px", flex: 1, overflowY: "auto" },
  noLead: { textAlign: "center", padding: "32px 16px" },
  noLeadIcon: { fontSize: "32px", color: "rgba(255,255,255,0.1)", marginBottom: "12px" },
  noLeadText: { color: "rgba(255,255,255,0.25)", fontSize: "12px", lineHeight: 1.6 },
  leadCard: {
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "12px", padding: "16px",
  },
  scoreBadge: {
    borderRadius: "8px", padding: "8px 12px",
    fontWeight: 700, fontSize: "13px", textAlign: "center",
    marginBottom: "14px", letterSpacing: "0.5px",
  },
  leadRow: { marginBottom: "10px" },
  leadLabel: {
    display: "block", fontSize: "10px", fontWeight: 600,
    color: "rgba(255,255,255,0.35)", letterSpacing: "1px",
    textTransform: "uppercase", marginBottom: "2px",
  },
  leadVal: { fontSize: "13px", color: "rgba(255,255,255,0.85)", lineHeight: 1.4 },
  newChatBtn: {
    margin: "16px", padding: "12px",
    background: "rgba(201,169,110,0.1)", border: "1px solid rgba(201,169,110,0.3)",
    borderRadius: "10px", color: "#c9a96e", fontSize: "13px", fontWeight: 600,
    cursor: "pointer", fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.5px",
  },
  emailStatus: {
    margin: "12px 0 0",
    fontSize: "12px",
    color: "rgba(255,255,255,0.4)",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "8px 12px",
    background: "rgba(255,255,255,0.03)",
    borderRadius: "8px",
    border: "1px solid rgba(255,255,255,0.07)",
  },
  emailStatusDot: (color) => ({
    color,
    fontSize: "10px",
  }),

  // Chat
  chatArea: { flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", background: "#111e30" },
  chatHeader: {
    padding: "18px 24px",
    borderBottom: "1px solid rgba(255,255,255,0.06)",
    display: "flex", alignItems: "center", gap: "12px",
    background: "#0f1c2e",
  },
  agentDot: {
    width: "10px", height: "10px", borderRadius: "50%",
    background: "#4ade80", boxShadow: "0 0 8px rgba(74,222,128,0.5)",
  },
  agentName: { color: "#fff", fontWeight: 600, fontSize: "15px" },
  agentStatus: { color: "rgba(255,255,255,0.35)", fontSize: "12px", marginTop: "1px" },
  messages: {
    flex: 1, overflowY: "auto", padding: "24px 20px",
    display: "flex", flexDirection: "column", gap: "16px",
  },
  botRow: { display: "flex", alignItems: "flex-end", gap: "10px" },
  userRow: { display: "flex", justifyContent: "flex-end" },
  botAvatar: {
    width: "32px", height: "32px", borderRadius: "10px",
    background: "rgba(201,169,110,0.15)", border: "1px solid rgba(201,169,110,0.3)",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: "16px", color: "#c9a96e", flexShrink: 0,
    fontFamily: "'DM Serif Display', serif",
  },
  botBubble: {
    background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "4px 16px 16px 16px", padding: "12px 16px", maxWidth: "72%",
    color: "rgba(255,255,255,0.88)", fontSize: "14px", lineHeight: 1.6,
  },
  userBubble: {
    background: "linear-gradient(135deg, #c9a96e, #b8935a)",
    borderRadius: "16px 16px 4px 16px", padding: "12px 16px", maxWidth: "72%",
    color: "#fff", fontSize: "14px", lineHeight: 1.6, fontWeight: 500,
  },
  typingDots: { display: "flex", gap: "5px", alignItems: "center", padding: "2px 0" },
  dot: {
    width: "7px", height: "7px", borderRadius: "50%",
    background: "rgba(201,169,110,0.7)", display: "inline-block",
    animation: "bounce 1.2s infinite ease-in-out",
  },
  inputRow: {
    padding: "16px 20px", borderTop: "1px solid rgba(255,255,255,0.06)",
    display: "flex", gap: "10px", background: "#0f1c2e",
  },
  input: {
    flex: 1, padding: "13px 18px",
    background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: "12px", color: "#fff", fontSize: "14px", outline: "none",
    fontFamily: "'DM Sans', sans-serif",
  },
  sendBtn: {
    width: "46px", height: "46px", borderRadius: "12px",
    background: "linear-gradient(135deg, #c9a96e, #b8935a)",
    border: "none", color: "#fff", cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
};
